<script>
import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import PageHeader from "@/components/page-header";
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import Pagination from "../../../components/pagination.vue";
import Swal from "sweetalert2";
import formatter from "../../../mixins/formatter";

export default {
    page: {
        title: "Archive",
        meta: [{ name: "description", content: appConfig.description }],
    },
    components: { Layout, PageHeader, Pagination },
    mixins: [formatter],
    data() {
        return {
            title: "Archive",
            loading: false,
            selectedYear: null,
            archiveYears: [],
            selectedButton: "All",
            selectedButtonValue: 0,
            buttons: [
                { label: 'All', value: 0 },
                { label: 'Early Bird', value: 1 },
                { label: 'Local', value: 2 },
                { label: 'National', value: 3 },
                { label: 'Late Born', value: 4 },
                { label: 'Genesis', value: 5 },
            ],
            search: {
                chapter: "",
                stag_registry: "",
                breeders: "",
                wingband_id: "",
            },
            filter: { limit: 10 },
            data: {
                list: [],
                total: 0,
                links: [],
                current_page: 1,
                per_page: 1,
                last_page: 1,
            },
            selectedWingbands: [],
            allChapters: [],
        };
    },
    computed: {
        ...mapGetters("auth", ["profile"]),
        availableChapters() {
            if (this.profile.role === 'admin') return this.allChapters;
            return this.profile.chapters || [];
        },
        allSelected() {
            return this.data.list.length > 0 && this.selectedWingbands.length === this.data.list.length;
        },
    },
    methods: {
        ...mapActions("archive", {
            getArchiveYears: "getYears",
            getArchiveList: "getList",
            restoreWingband: "restore",
            bulkRestoreWingband: "bulkRestore",
        }),
        async loadYears() {
            this.loading = true;
            var res = await this.getArchiveYears();
            this.loading = false;
            if (res && res.data) {
                this.archiveYears = res.data;
            }
        },
        selectYear(year) {
            this.selectedYear = year;
            this.initList(1);
        },
        backToYears() {
            this.selectedYear = null;
            this.data.list = [];
            this.data.total = 0;
            this.selectedWingbands = [];
        },
        selectButton(button) {
            this.selectedButton = button.label;
            this.selectedButtonValue = button.value;
            this.initList(1);
        },
        resetFilter() {
            this.search.chapter = "";
            this.search.stag_registry = "";
            this.search.breeders = "";
            this.search.wingband_id = "";
            this.selectedButton = "All";
            this.selectedButtonValue = 0;
            this.filter.limit = 10;
            this.initList(1);
        },
        async initList(p) {
            var pl = {
                page: p,
                limit: this.filter.limit,
                sort: "created_at",
                status: 2,
                wingband_year: this.selectedYear,
            };
            if (this.selectedButtonValue) pl.season = this.selectedButtonValue;
            if (this.search.stag_registry) pl.stag_registry = this.search.stag_registry;
            if (this.search.breeders) pl.breeder_name = this.search.breeders;
            if (this.search.wingband_id) pl.wingband_number = this.search.wingband_id;
            if (this.search.chapter) pl.chapter = this.search.chapter;

            this.loading = true;
            try {
                var res = await this.getArchiveList(pl);
                if (res && res.data) {
                    this.data.list = res.data || [];
                    this.data.total = res.total || 0;
                    this.data.links = res.links || [];
                    this.data.current_page = res.current_page || 1;
                    this.data.per_page = res.per_page || this.filter.limit;
                    this.data.last_page = res.last_page || 1;
                } else {
                    this.data.list = [];
                    this.data.total = 0;
                }
            } catch (e) {
                this.data.list = [];
                this.data.total = 0;
            }
            this.loading = false;
        },
        async restoreRow(id) {
            var confirmed = await Swal.fire({
                title: "Restore Record",
                text: "Are you sure you want to restore this archived record?",
                icon: "warning",
                showCancelButton: true,
            }).then(function(r) { return r.isConfirmed; });
            if (confirmed) {
                var res = await this.restoreWingband({ id: id });
                if (res && res.message) {
                    Swal.fire({ icon: "success", title: "Restored", text: res.message, timer: 2000, timerProgressBar: true });
                    this.initList(this.data.current_page);
                } else {
                    Swal.fire({ icon: "error", title: "Failed", text: "Could not restore record." });
                }
            }
        },
        selectRow(item) {
            var idx = this.selectedWingbands.indexOf(item._id);
            if (idx === -1) this.selectedWingbands.push(item._id);
            else this.selectedWingbands.splice(idx, 1);
        },
        toggleSelectAll() {
            if (this.allSelected) this.selectedWingbands = [];
            else this.selectedWingbands = this.data.list.map(function(r) { return r._id; });
        },
        async restoreSelected() {
            if (!this.selectedWingbands.length) return;
            var confirmed = await Swal.fire({
                title: "Restore Selected",
                text: "Restore " + this.selectedWingbands.length + " selected records?",
                icon: "warning",
                showCancelButton: true,
            }).then(function(r) { return r.isConfirmed; });
            if (confirmed) {
                var res = await this.bulkRestoreWingband({ wingband_ids: this.selectedWingbands });
                if (res && (res.status === 200 || res.status === 201)) {
                    Swal.fire({ icon: "success", title: "Restored", text: "Records restored successfully.", timer: 2000, timerProgressBar: true });
                    this.selectedWingbands = [];
                    this.initList(this.data.current_page);
                } else {
                    Swal.fire({ icon: "error", title: "Failed", text: "Could not restore records." });
                }
            }
        },
        async exportAll() {
            Swal.fire({ title: 'Exporting...', text: 'Fetching archived records...', allowOutsideClick: false, didOpen: function() { Swal.showLoading(); } });
            var pl = { page: 1, limit: 5000, sort: "created_at", status: 2, wingband_year: this.selectedYear };
            if (this.selectedButtonValue) pl.season = this.selectedButtonValue;
            if (this.search.stag_registry) pl.stag_registry = this.search.stag_registry;
            if (this.search.breeders) pl.breeder_name = this.search.breeders;
            if (this.search.wingband_id) pl.wingband_number = this.search.wingband_id;
            if (this.search.chapter) pl.chapter = this.search.chapter;
            try {
                var allRecords = [];
                var currentPage = 1;
                var lastPage = 1;
                do {
                    pl.page = currentPage;
                    var res = await this.getArchiveList(pl);
                    var pageData = (res && res.data) ? res.data : [];
                    allRecords = allRecords.concat(pageData);
                    lastPage = (res && res.last_page) ? res.last_page : 1;
                    currentPage++;
                } while (currentPage <= lastPage);
                if (allRecords.length === 0) { Swal.fire({ icon: 'info', title: 'No Data' }); return; }
                var csv = "STAG REGISTRY NO.,NAME OF BREEDERS,FARM NAME,FARM ADDRESS,PROVINCE,CHAPTER,CONTACT NO.,WINGBAND NO.,FEATHER COLOR,LEG COLOR,COMB SHAPE,NOSE MARKINGS,FEET MARKINGS,DATE,SEASON\n";
                allRecords.forEach(function(item) {
                    csv += [item.stag_registry, item.breeder_name, item.farm_name, item.farm_address, item.province, item.chapter, item.contact_number, item.wingband_number, item.feather_color, item.leg_color, item.comb_shape, item.nose_markings, item.feet_markings, item.wingband_date, item.season_name].map(function(v) { return '"' + String(v || '').replace(/"/g, '""') + '"'; }).join(',') + "\n";
                });
                var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                var link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = "Archive_" + this.selectedYear + ".csv";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                Swal.fire({ icon: 'success', title: 'Exported', text: allRecords.length + ' records exported.', timer: 2000, timerProgressBar: true });
            } catch (e) {
                Swal.fire({ icon: 'error', title: 'Export Failed' });
            }
        },
    },
    mounted() {
        this.loadYears();
        if (this.profile.role === 'admin') {
            this.$store.dispatch("chapter/getAll", { limit: 200 })
                .then(function(res) { this.allChapters = res && res.data ? (res.data.data || res.data) : []; }.bind(this));
        }
    },
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" />
        <div class="row">
            <div class="col-lg-12">
                <!-- YEAR LIST VIEW -->
                <div v-if="!selectedYear" class="card p-3">
                    <h4 class="fw-bolder mb-3">Available Archive Years</h4>
                    <div v-if="loading" class="text-center py-4">
                        <div class="spinner-border text-primary"></div>
                    </div>
                    <table v-else class="table table-bordered">
                        <thead class="bg-light">
                            <tr>
                                <th>Year</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="year in archiveYears" :key="year">
                                <td class="fw-bold">{{ year }}</td>
                                <td>
                                    <button class="btn btn-primary btn-sm" @click="selectYear(year)">View</button>
                                </td>
                            </tr>
                            <tr v-if="archiveYears.length === 0">
                                <td colspan="2" class="text-center text-muted py-3">No archived years found.</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="mt-2">
                        <small class="text-muted"><i class="bx bx-info-circle me-1"></i>Select a year to view archived entries.</small>
                    </div>
                </div>

                <!-- ARCHIVED RECORDS VIEW -->
                <div v-if="selectedYear">
                    <div class="card mb-2 pb-1">
                        <div class="card-body pb-2">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                    <h4 class="fw-bolder mb-0">ARCHIVE</h4>
                                    <span class="text-muted">Archive Year: {{ selectedYear }}</span>
                                </div>
                                <button class="btn btn-secondary" @click="backToYears()">
                                    <i class="bx bx-arrow-back me-1"></i>Back to Years
                                </button>
                            </div>
                            <h5 class="fw-bolder mt-3">Season</h5>
                            <div class="d-flex flex-wrap gap-2 mb-2">
                                <button
                                    v-for="(button, index) in buttons"
                                    :key="index"
                                    :class="['season-btn', 'season-btn--' + button.label.toLowerCase().replace(' ', '-'), { active: selectedButton === button.label }]"
                                    @click="selectButton(button)">
                                    {{ button.label }}
                                </button>
                            </div>
                            <hr>
                            <h5 class="fw-bolder mt-2">Information</h5>
                            <div class="col-12 d-flex flex-wrap">
                                <div class="col-12 col-lg-10">
                                    <div class="grid-2">
                                        <div class="disflex custom-border p-1">
                                            <label class="filter-label">Banding Year</label>
                                            <input type="text" class="form-control" disabled :value="selectedYear">
                                        </div>
                                        <div class="disflex custom-border p-1">
                                            <label class="filter-label">Stag Registry No.</label>
                                            <input v-model.lazy="search.stag_registry" type="text" class="form-control" placeholder="Enter stag registry no...">
                                        </div>
                                    </div>
                                    <div class="grid-2">
                                        <div class="disflex custom-border p-1">
                                            <label class="filter-label">Chapter</label>
                                            <select v-model="search.chapter" class="form-control">
                                                <option value="">-- Select Chapter --</option>
                                                <option v-for="c in availableChapters" :key="c.id" :value="c.chapter">{{ c.chapter }}</option>
                                            </select>
                                        </div>
                                        <div class="disflex custom-border p-1">
                                            <label class="filter-label">Wingband ID No.</label>
                                            <input v-model.lazy="search.wingband_id" type="text" class="form-control" placeholder="Enter wingband id no...">
                                        </div>
                                    </div>
                                    <div class="grid-2">
                                        <div class="disflex custom-border p-1">
                                            <label class="filter-label">Name of Breeders</label>
                                            <input v-model.lazy="search.breeders" type="text" class="form-control" placeholder="Enter name of breeders...">
                                        </div>
                                    </div>
                                </div>
                                <div class="col-12 col-lg-2">
                                    <div class="d-flex flex-row flex-lg-column justify-content-start justify-content-lg-center align-items-stretch h-100 gap-2">
                                        <button class="btn btn-success py-2 px-4" @click="initList(1)">Submit</button>
                                        <button class="btn btn-secondary py-2 px-4" @click="resetFilter()">Reset</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <h2 class="text-secondary text-center"><span v-if="data.total">{{ numToString(data.total) }}</span> Archived Entries</h2>

                    <div class="card p-0">
                        <div class="p-2 custom-overflow">
                            <div class="d-flex justify-content-between pb-2 flex-wrap gap-2">
                                <div class="d-flex align-items-center">
                                    <label class="filter-label">Limit:</label>
                                    <select class="form-control w-auto ms-1" v-model.lazy="filter.limit" @change="initList(1)">
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                        <option value="200">200</option>
                                        <option value="500">500</option>
                                        <option value="1000">1000</option>
                                        <option value="5000">5000</option>
                                    </select>
                                </div>
                                <div class="d-flex gap-2 flex-wrap" v-if="profile.role == 'admin'">
                                    <button class="btn btn-sm btn-outline-secondary" @click="toggleSelectAll()">Select All</button>
                                    <button class="btn btn-sm btn-danger" :disabled="!selectedWingbands.length" @click="restoreSelected()">
                                        <i class="bx bx-revision me-1"></i>Restore Selected
                                    </button>
                                    <button class="btn btn-sm btn-light" @click="exportAll()">
                                        <i class="bx bx-export me-1"></i>Export All
                                    </button>
                                </div>
                            </div>
                            <table class="mb-0 table">
                                <thead>
                                    <tr class="bg-light">
                                        <th v-if="profile.role == 'admin'">
                                            <input type="checkbox" class="form-check-input" :checked="allSelected" @change="toggleSelectAll">
                                        </th>
                                        <th>#</th>
                                        <th>Stag Registry No.</th>
                                        <th>Name of Breeders</th>
                                        <th>Farm Name</th>
                                        <th>Farm Address</th>
                                        <th>Province</th>
                                        <th>Chapter</th>
                                        <th>Contact No.</th>
                                        <th>Wingband No.</th>
                                        <th>Feather Color</th>
                                        <th>Leg Color</th>
                                        <th>Comb Shape</th>
                                        <th>Nose Markings</th>
                                        <th>Feet Markings</th>
                                        <th>Date</th>
                                        <th>Season</th>
                                        <th v-if="profile.role == 'admin'">Action</th>
                                    </tr>
                                </thead>
                                <tbody v-if="!loading">
                                    <tr v-for="(row, index) in data.list" :key="index">
                                        <td v-if="profile.role == 'admin'">
                                            <input type="checkbox" class="form-check-input" :checked="selectedWingbands.includes(row._id)" @change="selectRow(row)">
                                        </td>
                                        <td>{{ index + 1 + (data.current_page - 1) * data.per_page }}</td>
                                        <td>{{ row.stag_registry }}</td>
                                        <td>{{ row.breeder_name }}</td>
                                        <td>{{ row.farm_name }}</td>
                                        <td>{{ row.farm_address }}</td>
                                        <td>{{ row.province }}</td>
                                        <td>{{ row.chapter }}</td>
                                        <td>{{ row.contact_number }}</td>
                                        <td>{{ row.wingband_number }}</td>
                                        <td>{{ row.feather_color }}</td>
                                        <td>{{ row.leg_color }}</td>
                                        <td>{{ row.comb_shape }}</td>
                                        <td>{{ row.nose_markings }}</td>
                                        <td>{{ row.feet_markings }}</td>
                                        <td>{{ row.wingband_date }}</td>
                                        <td>{{ row.season_name }}</td>
                                        <td v-if="profile.role == 'admin'">
                                            <button class="btn btn-sm btn-outline-danger" @click="restoreRow(row._id)">Restore</button>
                                        </td>
                                    </tr>
                                    <tr v-if="data.list.length === 0">
                                        <td :colspan="profile.role == 'admin' ? 19 : 17" class="text-center text-muted py-3">No archived records found.</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div v-if="loading" class="text-center py-3">
                                <div class="spinner-border spinner-border-sm text-primary"></div> Loading...
                            </div>
                        </div>
                        <Pagination :data="data" @emitPage="initList" class="me-2" />
                    </div>

                    <div class="mt-2" v-if="profile.role == 'admin'">
                        <small class="text-muted"><i class="bx bx-info-circle me-1"></i>Only Admin users can restore archived records.</small>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<style scoped>
th, td { width: 75px !important; text-align: start; }
.table> :not(caption)>*>* { text-align: center; padding: .5rem .1rem !important; }
th { text-transform: uppercase; }

.season-btn { padding: 8px 20px; border: none; border-radius: 6px; font-weight: 700; font-size: 14px; cursor: pointer; color: #fff; opacity: 0.75; white-space: nowrap; }
.season-btn:hover { opacity: 0.9; }
.season-btn.active { opacity: 1; box-shadow: 0 3px 10px rgba(0,0,0,0.25); }
.season-btn--all { background-color: #343a40; }
.season-btn--early-bird { background-color: #6f42c1; }
.season-btn--local { background-color: #34c38f; }
.season-btn--national { background-color: #f1b44c; }
.season-btn--late-born { background-color: #adb5bd; color: #333; }
.season-btn--genesis { background-color: #0dcaf0; }
</style>
